import OSS from 'ali-oss';
import dayjs from 'dayjs';
import md5 from 'md5';

import { Toast } from '@whelk/components';
import { MultiModalComponentData } from '@whelk/const';
import { getFileRequestPolicy, getPolicyCallback } from '@whelk/services/src/chat';
import { PolicyInfo } from '@whelk/services/src/type/chat';
import { AppDispatch } from '@whelk/store';
import { updateFileInfo, updateStatus } from '@whelk/store/slices/uploadSlice';

import i18n, { getTextByLang } from './i18n';
import { trackCustomEvent } from './track';
let client: OSS | null = null;
const ONE_MIN = 60000;
const headers = {
  'Content-Disposition': 'attachment',
};
const options = {
  parallel: 4,
  partSize: 1024 * 1024,
  headers: {},
};

let stsPolicyRef: PolicyInfo | null = null;

// 根据服务器时间和服务器过期时间判断是否过期
const isExpired = (serverTime: string, expiration: string, localTime: string): boolean => {
  // 解析时间字符串
  const serverTimeDate = new Date(serverTime);
  const expirationDate = new Date(expiration);
  const localTimeDate = new Date(localTime);

  // 计算服务器时间和当前时间的时间差（毫秒）
  const timeDifference = localTimeDate.getTime() - serverTimeDate.getTime();
  // 校正服务器过期时间
  const adjustedExpirationDate = new Date(expirationDate.getTime() + timeDifference);

  return new Date().getTime() >= adjustedExpirationDate.getTime();
};

const getFileMD5 = (file: File) => {
  let res: (value: unknown) => void;
  const promise = new Promise((resolve) => {
    res = resolve;
  });
  const reader = new FileReader();
  reader.readAsArrayBuffer(file);
  reader.onload = (event) => {
    const fileArray = new Uint8Array(event.target?.result as any);
    const fileToMD5 = md5(fileArray);
    res(fileToMD5);
  };
  return promise;
};

const getFileID = async (fileInfo: MultiModalComponentData, dispatch: AppDispatch) => {
  const stsPolicy = stsPolicyRef as PolicyInfo;
  const file = fileInfo.file as File;

  const fileMD5 = await getFileMD5(file);
  const { fileID, ossPath } = await getPolicyCallback({
    fileName: fileInfo?.uuid + '.' + fileInfo.file_type,
    originFileName: file.name,
    dir: stsPolicy.dir,
    endpoint: stsPolicy.endpoint,
    bucketName: stsPolicy.bucketName,
    size: file.size + '',
    mimeType: file.type,
    fileMd5: fileMD5 as string,
  });
  dispatch(updateFileInfo({ ossPath: ossPath, fileID: fileID, uuid: fileInfo.uuid! }));
};

export const initClient = async () => {
  const credentials = await getFileRequestPolicy();
  const serverTime = dayjs(credentials.serverTime);
  const expiration = dayjs(credentials.expiration);
  stsPolicyRef = credentials;
  stsPolicyRef.localTime = new Date().toString();
  // 计算时间差并返回毫秒数
  const diffInMilliseconds = expiration.diff(serverTime);
  // 注册oss
  client = new OSS({
    endpoint: credentials?.endpoint,
    accessKeyId: credentials?.accessKeyId,
    accessKeySecret: credentials?.accessKeySecret,
    stsToken: credentials?.securityToken,
    bucket: credentials?.bucketName,
    refreshSTSTokenInterval: diffInMilliseconds - ONE_MIN * 6,
    refreshSTSToken: async () => {
      // 刷新凭证
      const info = await getFileRequestPolicy();
      stsPolicyRef = info;
      stsPolicyRef.localTime = new Date().toString();
      return {
        accessKeyId: info.accessKeyId,
        accessKeySecret: info.accessKeySecret,
        stsToken: info.securityToken,
        bucket: info.bucketName,
        refreshSTSTokenInterval: ONE_MIN * 51,
      };
    },
  });
};

const multipartUpload = async (
  fileInfo: MultiModalComponentData,
  dispatch: AppDispatch,
  selectedChatID: string,
) => {
  if (!fileInfo.file) return;
  const requestStartTime = Date.now();

  // 如果文件类型是.txt 则添加header进行强制下载
  options.headers = fileInfo.file_type === 'txt' ? headers : {};

  // 背景：oss sts 过期会重新获取sts数据 同时会拉下来 dir文件目录，但是文件目录会更新。传入multipartUpload方法中的数据不会更新，导致文件目录传递一场
  // 根据oss代码中文件名的获取会执行replace方法，故重写replace方法，保证获取到最新的数据

  const fileNameReplace = {
    replace: (...arg: any) => {
      const text = stsPolicyRef?.dir + '/' + fileInfo?.uuid + '.' + fileInfo.file_type;
      //@ts-ignore
      return text.replace(...arg);
    },
  };
  const file = fileInfo.file;
  try {
    const res = await client?.multipartUpload(
      // @ts-ignore
      fileNameReplace,
      file,
      {
        ...options,
        progress: () => {
          // dispatch(
          //   updateProgress({
          //     progress: p,
          //     uuid: fileInfo.uuid,
          //     cpt: cpt,
          //   }),
          // );
          // setAbortCheckpoint(cpt);
          // setProgress(p);
        },
      },
    );
    trackCustomEvent({
      id: 'file_upload',
      chat_id: selectedChatID || '',
      file_size: fileInfo.file_size!,
      zip_file_size: res?.res.size,
      file_type: fileInfo.file_type!,
      response_time: Date.now() - requestStartTime,
      is_retry: 2,
      upload_type: fileInfo.upload_type!,
      status: 1,
      statusCode: res?.res.status,
      page: selectedChatID ? 'chat_page' : 'home_page',
    });

    if (res) {
      await getFileID(fileInfo, dispatch);
      dispatch(updateStatus({ status: 'done', uuid: fileInfo.uuid! }));
    } else {
      trackCustomEvent({
        id: 'file_upload',
        status: 2,
        reason: 'oss 分片上传异常',
        statusCode: '未知状态码',
        file_size: fileInfo.file_size!,
        file_type: fileInfo.file_type!,
        upload_type: fileInfo.upload_type!,
        chat_id: selectedChatID || '',
        response_time: Date.now() - requestStartTime,
        is_retry: 2,
        page: selectedChatID ? 'chat_page' : 'home_page',
      });
      dispatch(updateStatus({ status: 'retry', uuid: fileInfo.uuid! }));
      Toast.show(getTextByLang('toast_upload_fail', i18n.language));
    }
    return res;
  } catch (err: any) {
    trackCustomEvent({
      id: 'file_upload',
      status: 2,
      reason: err?.message || '未知原因',
      statusCode: err?.code ? String(err?.code) : '未知状态码',
      file_size: fileInfo.file_size!,
      file_type: fileInfo.file_type!,
      upload_type: fileInfo.upload_type!,
      chat_id: selectedChatID || '',
      response_time: Date.now() - requestStartTime,
      is_retry: 2,
      page: selectedChatID ? 'chat_page' : 'home_page',
    });
    dispatch(updateStatus({ status: 'retry', uuid: fileInfo.uuid! }));
    Toast.show(getTextByLang('toast_upload_fail', i18n.language));
  }
};

export const createMultipartUpload = async ({
  fileInfo,
  selectedChatID,
  dispatch,
}: {
  fileInfo: MultiModalComponentData;
  selectedChatID: string;
  dispatch: AppDispatch;
}) => {
  if (
    !client ||
    (stsPolicyRef &&
      isExpired(stsPolicyRef.serverTime, stsPolicyRef.expiration, stsPolicyRef?.localTime))
  )
    await initClient();
  multipartUpload(fileInfo, dispatch, selectedChatID);
};
